import React from "react";
import { HiAnnotation, HiDotsVertical, HiEye, HiPencil, HiTrash } from "react-icons/hi";
import type { StrategyDetailActions } from "../../../hooks/useStrategyDetailType";
import { useTranslation } from "@repo/core";

const SmallScreenActionMenu = ({
  menuRef,
  isMenuOpen,
  toggleMenu,
  actions,
  handleEditClick,
  handleRemoveClick,
  handleShowDraftClick,
  handleShowActiveClick,
  handleAddNoteClick,
  hasNote,
  setIsMenuOpen,
  modelId,
}: {
  menuRef: React.RefObject<HTMLDivElement | null>;
  isMenuOpen: boolean;
  toggleMenu: () => void;
  actions: StrategyDetailActions;
  handleEditClick: () => void;
  handleRemoveClick: () => void;
  handleShowDraftClick: () => void;
  handleShowActiveClick: () => void;
  handleAddNoteClick: () => void;
  hasNote: boolean;
  setIsMenuOpen: (isOpen: boolean) => void;
  modelId?: string;
}) => {
  const { tCommon, tStrategy } = useTranslation();
  const isDraft = modelId?.includes('_draft');

  return (
    <div className="lg:hidden relative" ref={menuRef}>
      <button
        onClick={toggleMenu}
        className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
      >
        <HiDotsVertical size={20} />
      </button>

      {/* Dropdown menu */}
      {isMenuOpen && (
        <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
          <div className="py-1">
            {actions.showEdit && (
              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  handleEditClick();
                }}
                className="flex items-center gap-2 w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <HiPencil size={16} className="text-blue-600" />
                <span className="text-sm">{tCommon("edit")}</span>
              </button>
            )}
            {actions.showEdit && !hasNote && (
              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  handleAddNoteClick();
                }}
                className="flex items-center gap-2 w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <HiAnnotation size={16} className="text-blue-600" />
                <span className="text-sm">{tStrategy("note.addButton")}</span>
              </button>
            )}
            {actions.showDelete && (
              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  handleRemoveClick();
                }}
                className="flex items-center gap-2 w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <HiTrash size={16} className="text-red-600" />
                <span className="text-sm">{isDraft ? tCommon("deleteDraft") : tCommon("delete")}</span>
              </button>
            )}
            {actions.showDraft && (
              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  handleShowDraftClick();
                }}
                className="flex items-center gap-2 w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <HiEye size={16} className="text-green-600" />
                <span className="text-sm">{tStrategy("showDraft")}</span>
              </button>
            )}
            {actions.showActive && (
              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  handleShowActiveClick();
                }}
                className="flex items-center gap-2 w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <HiEye size={16} className="text-blue-600" />
                <span className="text-sm">{tStrategy("showActive")}</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SmallScreenActionMenu;
